import { Price } from '@faststore/ui'
import { Fragment, useEffect, type ReactNode } from 'react'
import { useStoreContext } from 'src/contexts/store-context'
import { useFormattedPrice } from 'src/sdk/product/useFormattedPrice'
import { getPriceInfo } from 'src/utils'
import { getImageResized } from 'src/utils/getImageResized'
import { CategoryGridIcon } from '../icons/CategoryGridIcon'
import { ChevronRightIcon } from '../icons/ChevronRightIcon'
import { GoToAutocompleteItemIcon } from '../icons/GoToAutocompleteItemIcon'
import { MagnifyGlassMobile } from '../icons/MagnifyGalssMobile'
import type {
  AutocompleteAnalyticsData,
  SuggestionHit,
  TopicHit,
  VariantHit,
} from './algolia'
import {
  mainIndex,
  querySuggestionsIndex,
  seasonalTrendsIndex,
} from './algoliaSearchConfig'
import { customFormatSearchPath, useSelectItemSearchBar } from './utils'

const SEARCHBAR_ROW_ICON_COLOR = '#75777D'
const SEARCHBAR_ROW_ICON_SIZE = 16

const AutocompleteLeadingRow = ({
  href,
  onClick,
  leadingIcon,
  children,
}: {
  href: string
  onClick?: () => void
  leadingIcon?: ReactNode
  children: ReactNode
}) => {
  return (
    <a
      href={href}
      onClick={onClick}
      className={`border-0 text-[15px] text-black no-underline flex items-center gap-3`}
    >
      {leadingIcon ? (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
          {leadingIcon}
        </span>
      ) : null}
      <span className={`flex-1 ${leadingIcon ? 'min-w-0' : ''}`}>
        {children}
      </span>
      <GoToAutocompleteItemIcon />
    </a>
  )
}

const formatSuggestionLabel = (suggestion: string) => {
  const trimmed = suggestion.trim()
  if (!trimmed) return suggestion

  return trimmed.charAt(0).toLocaleUpperCase('it-IT') + trimmed.slice(1)
}

const highlightSuggestion = (suggestion: string, query: string): ReactNode => {
  const displaySuggestion = formatSuggestionLabel(suggestion)
  const trimmed = query.trim()

  if (!trimmed) {
    return <span className="text-black">{displaySuggestion}</span>
  }

  const lowerSuggestion = displaySuggestion.toLowerCase()

  // Spezza la query in parole (token), rimuovendo eventuali duplicati/vuoti
  const words = Array.from(
    new Set(trimmed.toLowerCase().split(/\s+/).filter(Boolean))
  )

  // Trova tutte le occorrenze (start, end) di ciascuna parola nel suggerimento
  const ranges: Array<[number, number]> = []

  for (const word of words) {
    let from = 0
    let foundAt: number

    while ((foundAt = lowerSuggestion.indexOf(word, from)) !== -1) {
      ranges.push([foundAt, foundAt + word.length])
      from = foundAt + word.length
    }
  }

  if (ranges.length === 0) {
    return <span className="text-black font-bold">{displaySuggestion}</span>
  }

  // Ordina e unisce gli intervalli sovrapposti/adiacenti
  ranges.sort((a, b) => a[0] - b[0])

  const merged: Array<[number, number]> = []
  for (const [start, end] of ranges) {
    const last = merged[merged.length - 1]
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end)
    } else {
      merged.push([start, end])
    }
  }

  // Costruisce i nodi alternando testo "plain" (bold) e testo "match" (non bold)
  const nodes: ReactNode[] = []
  let cursor = 0

  merged.forEach(([start, end], i) => {
    if (start > cursor) {
      nodes.push(
        <span key={`plain-${i}`} className="text-black font-bold">
          {displaySuggestion.slice(cursor, start)}
        </span>
      )
    }

    nodes.push(
      <span key={`match-${i}`} className="text-black">
        {displaySuggestion.slice(start, end)}
      </span>
    )

    cursor = end
  })

  if (cursor < displaySuggestion.length) {
    nodes.push(
      <span key="rest" className="text-black font-bold">
        {displaySuggestion.slice(cursor)}
      </span>
    )
  }

  return <Fragment>{nodes}</Fragment>
}

export const AutocompleteSuggestionItem = ({
  item,
  query,
  onSelect,
  showLeadingIcon = false,
  objectIDs,
  index,
  sendAutoCompleteAnalyticsEvent,
  isOpen,
}: {
  item: SuggestionHit
  query: string
  onSelect: () => void
  showLeadingIcon?: boolean
  objectIDs: Array<{ object_id: string; suggestion_value: string }>
  index: number
  sendAutoCompleteAnalyticsEvent: (eventData: AutocompleteAnalyticsData) => void
  isOpen: boolean
}) => {
  useEffect(() => {
    if (index > 0 || !isOpen) return

    sendAutoCompleteAnalyticsEvent({
      topic: 'query_suggestion',
      objectIDs,
      queryID: item.__queryID,
      type: 'view',
      state: 'typing',
    })

    window.aa('viewedObjectIDs', {
      eventType: 'view',
      eventName: 'Autocomplete suggestion shown',
      index: querySuggestionsIndex,
      objectIDs: objectIDs.map((obj) => obj.object_id),
      query,
    })
  }, [query, isOpen])

  return (
    <AutocompleteLeadingRow
      href={customFormatSearchPath(item.query)}
      onClick={() => {
        sendAutoCompleteAnalyticsEvent({
          topic: 'query_suggestion',
          objectIDs: [
            { object_id: item.objectID, suggestion_value: item.query },
          ],
          queryID: item.__queryID,
          type: 'click',
          state: 'typing',
        })
        window.aa('clickedObjectIDsAfterSearch', {
          eventType: 'click',
          eventName: 'Autocomplete suggestion clicked',
          index: querySuggestionsIndex,
          objectIDs: [item.objectID],
          query,
          queryID: item.__queryID,
          positions: [index + 1],
        })
        onSelect()
        // sendSearchPreviewClickEvent('Suggeriti per te', item.query, userQuery)
      }}
      leadingIcon={
        showLeadingIcon ? (
          <MagnifyGlassMobile
            width={SEARCHBAR_ROW_ICON_SIZE}
            height={SEARCHBAR_ROW_ICON_SIZE}
            color={SEARCHBAR_ROW_ICON_COLOR}
          />
        ) : undefined
      }
    >
      {highlightSuggestion(item.query, query)}
    </AutocompleteLeadingRow>
  )
}

export const AutocompleteCategoryItem = ({
  href,
  label,
  onSelect,
  showLeadingIcon = false,
  sendAutoCompleteAnalyticsEvent,
  objectIDs,
  isOpen,
  index,
}: {
  href: string
  label: string
  onSelect?: () => void
  showLeadingIcon?: boolean
  sendAutoCompleteAnalyticsEvent: (eventData: AutocompleteAnalyticsData) => void
  objectIDs: Array<{ object_id?: string; suggestion_value: string }>
  isOpen: boolean
  index: number
}) => {
  useEffect(() => {
    if (index > 0 || !isOpen) return

    sendAutoCompleteAnalyticsEvent({
      topic: 'suggested_categories',
      objectIDs,
      queryID: '',
      type: 'view',
      state: 'typing',
    })
  }, [isOpen])

  return (
    <AutocompleteLeadingRow
      href={href}
      onClick={() => {
        sendAutoCompleteAnalyticsEvent({
          topic: 'suggested_categories',
          objectIDs: [{ suggestion_value: label }],
          queryID: '',
          type: 'click',
          state: 'typing',
        })
        onSelect?.()
      }}
      leadingIcon={
        showLeadingIcon ? (
          <CategoryGridIcon
            width={SEARCHBAR_ROW_ICON_SIZE}
            height={SEARCHBAR_ROW_ICON_SIZE}
          />
        ) : undefined
      }
    >
      {label}
    </AutocompleteLeadingRow>
  )
}

// Riga prodotto: alto livello Figma (img + nome + prezzo)
export const AutocompleteProductItem = ({
  item,
  onSelect,
  query,
  objectIDs,
  pageSize,
  isOpen,
}: {
  item: VariantHit
  onSelect: () => void
  query: string
  objectIDs: string[]
  pageSize: number
  isOpen: boolean
}) => {
  const { handleOnClick } = useSelectItemSearchBar(item) || {}
  const { favouriteStore } = useStoreContext()
  const image = item.images?.[0]
  const imageResized = getImageResized(image.image_url, 48, 48)
  // Stessa logica PLP: badge rosso se prezzo < omnibus (o list se <30gg)
  const priceInfo = getPriceInfo(
    {
      listPrice: item.list_price,
      lowPrice: item.selling_price,
      // unitMultiplier: item.unit_multiplier,
      unitMultiplier: null,
      minPriceThirtyDays: JSON.stringify({
        thirtyDaysPrice: item.omnibus_price,
        showMinPrice: item.show_omnibus_price,
      }),
      skuId: item.skuId,
    },
    true
  )

  const hasStockInStore =
    !!favouriteStore?.id && item.stores_in_stock?.includes(favouriteStore.id)

  useEffect(() => {
    if (item.__position > 1 || !isOpen) return

    window.aa('viewedObjectIDs', {
      eventType: 'view',
      eventName: 'Search result shown',
      index: mainIndex,
      objectIDs,
      query,
    })
  }, [query, isOpen])

  return (
    <a
      href={`${item.variant_url}`}
      onClick={() => {
        handleOnClick?.(query, pageSize)
        window.aa('clickedObjectIDsAfterSearch', {
          eventType: 'click',
          eventName: 'Search result clicked',
          index: mainIndex,
          objectIDs: [item.objectID],
          query,
          queryID: item.__queryID,
          positions: [item.__position],
        })
        onSelect()
        // sendSearchPreviewClickEvent(
        //   'I migliori risultati per',
        //   item.productName,
        //   userQuery
        // )
        // handleEvent({
        //   name: 'search_select_item',
        //   params: {
        //     url: window.location.href,
        //     term: userQuery,
        //     // As-is usa la posizione in lista: `__position` (InstantSearch).
        //     items: [
        //       {
        //         item_id: item.productId,
        //         index:
        //           (item as VariantHit & { __position?: number }).__position ??
        //           item.index,
        //       },
        //     ],
        //   },
        // })
      }}
      className="flex items-center gap-3 border-0 no-underline text-inherit"
    >
      {image?.image_url ? (
        <img
          src={imageResized}
          alt={image.image_text || item.productName}
          width={48}
          height={48}
          loading="lazy"
          className="h-12 w-12 shrink-0 rounded-sm object-contain bg-[#FAFAFA]"
        />
      ) : (
        <div className="h-12 w-12 shrink-0 rounded-sm bg-[#F0F0F0]" />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
        <span className="uppercase text-[10px] font-bold text-[#75777d]">
          {item.brand}
        </span>
        <span className="text-sm font-normal leading-[130%] text-black line-clamp-2">
          {item.productName}
        </span>
        <div className="flex items-center gap-2">
          <Price
            value={priceInfo.verifiedPriceToShow}
            formatter={useFormattedPrice}
            variant="spot"
            className={`${
              priceInfo.isDiscount ? 'text-white bg-red-500 px-1.5' : ''
            } block w-fit text-sm font-bold`}
          />
          {hasStockInStore && (
            <div className="flex items-center justify-center rounded-full py-[3px] px-2 bg-[rgba(0,182,122,0.1)] font-semibold text-[11px] text-[#00B579]">
              Ritiro 1h · disponibile
            </div>
          )}
        </div>
      </div>
      <ChevronRightIcon />
    </a>
  )
}

export const AutocompleteTopicItem = ({
  topic,
  index,
  objectIDs,
  sendAutoCompleteAnalyticsEvent,
  isOpen,
}: {
  topic: TopicHit
  index: number
  objectIDs: Array<{ object_id: string; suggestion_value: string }>
  sendAutoCompleteAnalyticsEvent: (eventData: AutocompleteAnalyticsData) => void
  isOpen: boolean
}) => {
  useEffect(() => {
    if (index > 0 || !isOpen) return

    sendAutoCompleteAnalyticsEvent({
      topic: 'trending_topics',
      objectIDs,
      queryID: topic.__queryID,
      type: 'view',
      state: 'empty',
    })

    window.aa('viewedObjectIDs', {
      eventType: 'view',
      eventName: 'Trending topic shown',
      index: seasonalTrendsIndex,
      objectIDs: objectIDs.map((obj) => obj.object_id),
    })
  }, [isOpen])

  return (
    <a
      href={topic.url_landing}
      onClick={() => {
        sendAutoCompleteAnalyticsEvent({
          topic: 'trending_topics',
          objectIDs: [
            { object_id: topic.objectID, suggestion_value: topic.titolo },
          ],
          queryID: topic.__queryID,
          type: 'click',
          state: 'empty',
        })
        window.aa('clickedObjectIDsAfterSearch', {
          eventType: 'click',
          eventName: 'Trending topic clicked',
          index: seasonalTrendsIndex,
          objectIDs: [topic.objectID],
          queryID: topic.__queryID,
          positions: [index + 1],
        })
      }}
      className="flex items-center gap-2 justify-between border-0 text-[15px] text-black no-underline"
    >
      <span>{topic.sottotitolo ?? topic.titolo}</span>
      <GoToAutocompleteItemIcon />
    </a>
  )
}

// export const AutocompleteSearchHistoryItems = ({
//   sendAutoCompleteAnalyticsEvent,
//   isOpen,
//   searchHistory,
//   deleteFromSearchHistory,
//   clearSearchHistory,
// }: {
//   sendAutoCompleteAnalyticsEvent: (eventData: AutocompleteAnalyticsData) => void
//   isOpen: boolean
//   searchHistory: string[]
//   deleteFromSearchHistory: (term: string) => void
//   clearSearchHistory: () => void
// }) => {
//   const hasSentSearchHistory = useRef(false)

//   const handleClick = (historyItem: string) => {
//     sendAutoCompleteAnalyticsEvent({
//       topic: 'search_history',
//       objectIDs: [{ suggestion_value: historyItem }],
//       queryID: '',
//       type: 'click',
//       state: 'empty',
//     })
//   }

//   useEffect(() => {
//     if (!isOpen || !searchHistory.length || hasSentSearchHistory.current) return

//     sendAutoCompleteAnalyticsEvent({
//       topic: 'search_history',
//       objectIDs: searchHistory.map((item) => ({ suggestion_value: item })),
//       queryID: '',
//       type: 'view',
//       state: 'empty',
//     })

//     hasSentSearchHistory.current = true
//   }, [isOpen, searchHistory])

//   return (
//     <>
//       <div className="flex gap-2 items-center justify-between">
//         <SectionTitle>Ricerche recenti</SectionTitle>
//         <button
//           onClick={clearSearchHistory}
//           className="flex items-center justify-center cursor-pointer outline-none bg-transparent border-none text-[#74767c] text-[13px] font-semibold"
//         >
//           Cancella
//         </button>
//       </div>

//       <ul className="flex flex-col gap-4 text-[15px]">
//         {searchHistory.slice(0, 2).map((historyItem, index) => {
//           return (
//             <li
//               className="flex gap-2 items-center justify-between"
//               key={`${historyItem}-${index}`}
//             >
//               <div className="flex gap-2 items-center">
//                 <SearchHistoryIcon />
//                 <a
//                   href={customFormatSearchPath(historyItem)}
//                   onClick={() => handleClick(historyItem)}
//                   className="break-all"
//                 >
//                   {historyItem}
//                 </a>
//               </div>
//               <button
//                 onClick={() => deleteFromSearchHistory(historyItem)}
//                 className="flex items-center justify-center cursor-pointer outline-none bg-transparent border-none"
//               >
//                 <CloseIcon width={18} height={18} color="#75777D" />
//               </button>
//             </li>
//           )
//         })}
//       </ul>
//     </>
//   )
// }
